import {
	AfterContentChecked,
	AfterContentInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChildren,
	inject,
	OnDestroy,
	QueryList,
	TrackByFunction,
} from "@angular/core";
import { CarouselItemComponent } from "common/carousel/carousel-item.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NgTemplateOutlet } from "@angular/common";

type SlidePosition = "left" | "center";

@Component({
	selector: "stages-carousel",
	styleUrls: ["./carousel.component.scss"],
	templateUrl: "./carousel.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgTemplateOutlet],
})
export class CarouselComponent implements AfterContentInit, AfterContentChecked, OnDestroy {
	private chRef = inject(ChangeDetectorRef);

	private destroy$ = new Subject<boolean>();
	private slideFrameId?: number;

	selection?: CarouselItemComponent;
	visibleItems: CarouselItemComponent[] = [];
	slidePosition: SlidePosition = "center";
	transitionEnabled = false;

	// TODO: Skipped for migration because:
	//  There are references to this query that cannot be migrated automatically.
	@ContentChildren(CarouselItemComponent) items!: QueryList<CarouselItemComponent>;

	ngAfterContentInit(): void {
		this.updateInitialSelection();

		this.items.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateInitialSelection();
		});
	}

	ngAfterContentChecked(): void {
		this.items.forEach((item: CarouselItemComponent, index: number) => {
			item.position = index;
		});
	}

	ngOnDestroy(): void {
		if (this.slideFrameId !== undefined) {
			cancelAnimationFrame(this.slideFrameId);
		}

		this.destroy$.next(true);
		this.destroy$.complete();
	}

	select(name: string, isAnimate: boolean = true): void {
		const newSelection = this.items.find((item) => item.name() === name);
		if (newSelection === undefined) {
			return;
		}

		if (this.selection === undefined) {
			this.selection = newSelection;
			this.visibleItems = [newSelection];
			this.slidePosition = "center";
			this.transitionEnabled = false;
			return;
		}

		if (this.selection === newSelection) {
			return;
		}

		if (!isAnimate) {
			this.selection = newSelection;
			this.visibleItems = [newSelection];
			this.slidePosition = "center";
			this.transitionEnabled = false;
			return;
		}

		const previousSelection = this.selection;
		const addRight = previousSelection.position < newSelection.position;

		this.selection = newSelection;

		if (this.slideFrameId !== undefined) {
			cancelAnimationFrame(this.slideFrameId);
			this.slideFrameId = undefined;
		}

		this.transitionEnabled = false;

		if (addRight) {
			// Forward:
			// Render [old, new].
			// Start with old visible, then slide left to new.
			this.visibleItems = [previousSelection, newSelection];
			this.slidePosition = "center";
			this.chRef.detectChanges();

			this.slideFrameId = requestAnimationFrame(() => {
				this.slideFrameId = requestAnimationFrame(() => {
					this.transitionEnabled = true;
					this.slidePosition = "left";
					this.chRef.detectChanges();
				});
			});
		} else {
			// Backward:
			// Render [new, old].
			// Start with old visible on the right, then slide right to new.
			this.visibleItems = [newSelection, previousSelection];
			this.slidePosition = "left";
			this.chRef.detectChanges();

			this.slideFrameId = requestAnimationFrame(() => {
				this.slideFrameId = requestAnimationFrame(() => {
					this.transitionEnabled = true;
					this.slidePosition = "center";
					this.chRef.detectChanges();
				});
			});
		}
	}

	onSlideTransitionEnd(event: TransitionEvent): void {
		if (event.propertyName !== "transform") {
			return;
		}

		if (!this.selection) {
			return;
		}

		this.visibleItems = [this.selection];
		this.slidePosition = "center";
		this.transitionEnabled = false;
		this.chRef.detectChanges();
	}

	getName: TrackByFunction<CarouselItemComponent> = (_index, item) => {
		return item.name();
	};

	private updateInitialSelection(): void {
		if (this.selection && this.items.toArray().includes(this.selection)) {
			return;
		}

		this.selection = this.items.length > 0 ? this.items.toArray()[0] : undefined;
		this.visibleItems = this.selection ? [this.selection] : [];
		this.slidePosition = "center";
		this.transitionEnabled = false;
		this.chRef.detectChanges();
	}
}
